/**
 * Page-spec overlays — runtime augmentations applied on top of the
 * crawled `php-page-specs.generated.json` so we can fix or extend
 * individual pages without re-running the crawler.
 *
 * Each overlay receives the raw spec for its target page plus the full
 * specs map (so it can borrow columns/forms from sibling pages) and
 * returns a (possibly mutated) spec. Overlays are pure: clone before
 * mutating. They run after `findSpec(route)` resolves the base spec.
 */

interface AnySpec {
  route: string;
  module_slug?: string | null;
  init: Record<string, string>;
  toolbar: { refresh: boolean; buttons: unknown[] };
  cols: Array<Record<string, unknown>>;
  action_forms?: Record<string, unknown> | null;
  // Other fields copied verbatim — we don't care for overlay purposes.
  [k: string]: unknown;
}

type Overlay = (base: AnySpec, all: readonly AnySpec[]) => AnySpec;

/**
 * Hand-written form specs for the user.user_operation action URLs that
 * the legacy PHP "跳转提示"/redirect responses prevented the crawler
 * from extracting automatically. Verified against
 *   GET /admin/user.user_operation/edit{N}.html?id=<sample>
 * on 2026-04-22.
 *
 *   edit       (重置登录密码)  — instant POST, no fields
 *   edit1      (清空密保信息)  — instant POST, no fields
 *   edit2      (重置交易密码)  — instant POST, no fields
 *   edit3      (冻结/解冻账户) — form: freeze (select date-offset) + freeze_remark (text, required)
 *                                Verified PHP option values (date offsets relative to "今天"):
 *                                  +3 / +7 / +30 / +90 / +36500 / -1 (解冻)
 *                                Anything else (e.g. "0"/"1") makes PHP silently
 *                                accept the form but never updates `freeze`.
 *   edit4      (每日EP1/EP2限额) — form: days_ep_limit (number, required)
 *   edit5      (红心操作)       — form: blood (number, required)
 */
const USER_OPERATION_FORMS: Record<string, unknown> = {
  edit3_url: {
    submit_url: null,
    tabs: [{
      title: '冻结/解冻账户',
      fields: [
        {
          name: 'freeze',
          label: '冻结操作',
          type: 'select',
          required: true,
          hint: '选择冻结时长（自今日起算），或选择"解冻"立即解除冻结。',
          options: [
            { value: '+3', label: '冻结账户 3 天' },
            { value: '+7', label: '冻结账户 7 天' },
            { value: '+30', label: '冻结账户 30 天' },
            { value: '+90', label: '冻结账户 90 天' },
            { value: '+36500', label: '永久冻结账户' },
            { value: '-1', label: '解冻' },
          ],
        },
        { name: 'freeze_remark', label: '冻结原因备注', type: 'text', required: true, placeholder: '冻结/解冻原因' },
      ],
    }],
  },
  edit4_url: {
    submit_url: null,
    tabs: [{
      title: '每日 EP1 / EP2 限额',
      fields: [
        { name: 'days_ep_limit', label: '每日 EP 限额', type: 'number', required: true, placeholder: '0 表示无限制' },
      ],
    }],
  },
  edit5_url: {
    submit_url: null,
    tabs: [{
      title: '红心数量',
      fields: [
        { name: 'blood', label: '红心数量', type: 'number', required: true },
      ],
    }],
  },
};

/** URL keys that are confirm-only (POST with just `id`, no extra fields). */
const USER_OPERATION_INSTANT = new Set(['edit_url', 'edit1_url', 'edit2_url']);

/**
 * For `user.user` (用户列表): merge in the operation columns + URL keys
 * from `user.UserOperation` (用户操作). Both pages list the same user
 * records (same `id` primary key), so 重置密码 / 冻结 / 限额 / 红心 buttons
 * work identically on either page. This matches the user expectation
 * that "manage a user" is one screen, not two.
 *
 * Also injects hand-written forms for the action URLs that the crawler
 * couldn't extract (edit3/4/5) and marks edit/edit1/edit2 as instant
 * actions so they fire as a single confirm-and-POST.
 *
 * IMPORTANT: `user.user` already has an `edit_url` (its own real edit
 * page) so we cannot blindly copy the user.UserOperation `edit_url` —
 * they would collide. Instead, rewrite all copied operat buttons to a
 * dedicated `op_*_url` namespace that points at the user.UserOperation
 * controller, leaving the host page's edit_url / delete_url intact.
 */
const OP_KEY_PREFIX = 'op_';
const renameOpKey = (k: string): string => OP_KEY_PREFIX + k;

function applyUserOperationButtons(target: AnySpec, op: AnySpec | undefined): void {
  if (!op) return;
  // Mirror every edit*_url from user_operation under the `op_` namespace
  // so they don't collide with the host page's own edit/delete URLs.
  for (const [k, v] of Object.entries(op.init)) {
    if (/^edit\d*_url$/.test(k) && typeof v === 'string') {
      const renamed = renameOpKey(k);
      if (!target.init[renamed]) target.init[renamed] = v;
    }
  }
  // Append operat columns the target is missing (matched by title) —
  // rewriting each button's url_key into the op_ namespace.
  const haveTitles = new Set(target.cols.map((c) => String(c.title ?? '')));
  for (const c of op.cols) {
    const isOperat = Array.isArray(c.operat) && c.operat.length > 0;
    if (!isOperat) continue;
    if (haveTitles.has(String(c.title ?? ''))) continue;
    const cloned = {
      ...c,
      operat: (c.operat as Array<Record<string, unknown>>).map((b) => ({
        ...b,
        url_key: typeof b.url_key === 'string' && /^edit\d*_url$/.test(b.url_key)
          ? renameOpKey(b.url_key)
          : b.url_key,
      })),
    };
    target.cols.push(cloned);
  }
  // Borrow any crawler-captured action_forms, re-keyed into op_ namespace.
  for (const [k, form] of Object.entries(op.action_forms ?? {})) {
    const renamed = /^edit\d*_url$/.test(k) ? renameOpKey(k) : k;
    if (!target.action_forms![renamed]) target.action_forms![renamed] = form;
  }
  // Then override with hand-written forms (keyed under op_ too) for the
  // POST-only redirect endpoints the crawler can't reach.
  for (const [k, form] of Object.entries(USER_OPERATION_FORMS)) {
    const renamed = renameOpKey(k);
    if (!target.action_forms![renamed]) target.action_forms![renamed] = form;
  }
  // Tag instant-action URLs (renamed) so the renderer fires a confirm
  // popup instead of opening a drawer.
  const tags = (target._instant_action_keys as string[] | undefined) ?? [];
  for (const k of USER_OPERATION_INSTANT) {
    const renamed = renameOpKey(k);
    if (!tags.includes(renamed)) tags.push(renamed);
  }
  target._instant_action_keys = tags;
}

const overlay_user_user: Overlay = (base, all) => {
  const op = all.find((s) => s.module_slug === 'user.UserOperation');
  if (!op) return base;
  const next: AnySpec = {
    ...base,
    init: { ...base.init },
    cols: [...base.cols],
    action_forms: { ...(base.action_forms ?? {}) },
  };
  applyUserOperationButtons(next, op);
  return next;
};

const overlay_user_useroperation: Overlay = (base) => {
  const next: AnySpec = {
    ...base,
    init: { ...base.init },
    cols: [...base.cols],
    action_forms: { ...(base.action_forms ?? {}) },
  };
  // Same hand-written forms apply directly to the user_operation page
  // (which is the canonical owner of these buttons).
  for (const [k, form] of Object.entries(USER_OPERATION_FORMS)) {
    if (!next.action_forms![k]) next.action_forms![k] = form;
  }
  const tags = (next._instant_action_keys as string[] | undefined) ?? [];
  for (const k of USER_OPERATION_INSTANT) {
    if (!tags.includes(k)) tags.push(k);
  }
  next._instant_action_keys = tags;
  return next;
};

const OVERLAYS: Record<string, Overlay> = {
  '/parity/user_user': overlay_user_user,
  '/parity/user_useroperation': overlay_user_useroperation,
};

export function applyOverlay<T extends AnySpec>(base: T, all: readonly T[]): T {
  const fn = OVERLAYS[base.route];
  if (!fn) return base;
  return fn(base, all) as T;
}
